import { batch } from 'react-redux';

import { handleErrors } from '../../actions/ErrorActions';
import { getNewNotifications } from '../../actions/NotificationsActions';
import { downloadFile } from '../../utils/domUtils';
import {
  APP_MIGRATION_IMPORT_APIS,
  IMPORT_POLLING_TIME,
  IMPORT_STEPS,
  REQUEST_NOTIFICATION_DELAY,
  STATUSES,
} from '../constants/import.constants';
import {
  getImportApiCall,
  getOverallImportStatus,
  preprocessImportStatusesRequest,
  preprocessImportStatusesResponse,
  updateImportStatusesWithCancelResponse,
  updateImportStatusesWithResponse,
} from '../utils/import.utils';

export const INITIATE_APP_MIGRATION_IMPORT_PROCESS = 'INITIATE_APP_MIGRATION_IMPORT_PROCESS';

export const SET_APP_MIGRATION_IMPORT_STEP = 'SET_APP_MIGRATION_IMPORT_STEP';
export const SET_APP_MIGRATION_IMPORT_DATA = 'SET_APP_MIGRATION_IMPORT_DATA';
export const SET_APP_MIGRATION_IMPORT_LOADING = 'SET_APP_MIGRATION_IMPORT_LOADING';
export const SET_APP_MIGRATION_IMPORT_ERROR = 'SET_APP_MIGRATION_IMPORT_ERROR';
export const CLEAR_APP_MIGRATION_IMPORT_ITEM = 'CLEAR_APP_MIGRATION_IMPORT_ITEM';
export const SET_APP_MIGRATION_MODAL_IN_FILE_PROCESSING_STATE =
  'SET_APP_MIGRATION_MODAL_IN_FILE_PROCESSING_STATE';

export const SET_APP_MIGRATION_UPLOAD_ITEMS = 'SET_APP_MIGRATION_UPLOAD_ITEMS';
export const CHANGE_APP_MIGRATION_IMPORT_STATUS = 'CHANGE_APP_MIGRATION_IMPORT_STATUS';
export const BULK_CHANGE_APP_MIGRATION_IMPORT_STATUSES =
  'BULK_CHANGE_APP_MIGRATION_IMPORT_STATUSES';

export const SET_APP_MIGRATION_IS_NEW_IMPORT = 'SET_APP_MIGRATION_IS_NEW_IMPORT';
export const SET_APP_MIGRATION_CURRENT_IMPORT_KEY = 'SET_APP_MIGRATION_CURRENT_IMPORT_KEY';

export const SET_APP_MIGRATION_ZIP_FIELD_ID = 'SET_APP_MIGRATION_ZIP_FIELD_ID';

export const SET_APP_MIGRATION_TOTAL_ITEMS_TO_IMPORT = 'SET_APP_MIGRATION_TOTAL_ITEMS_TO_IMPORT';
export const SET_APP_MIGRATION_PROCESSED_ITEMS_TO_IMPORT =
  'SET_APP_MIGRATION_PROCESSED_ITEMS_TO_IMPORT';

export const SET_APP_MIGRATION_FILE_PACKAGE = 'SET_APP_MIGRATION_FILE_PACKAGE';

export const setAppMigrationTotalItemsToImport = (importKey, count) => ({
  type: SET_APP_MIGRATION_TOTAL_ITEMS_TO_IMPORT,
  payload: { importKey, count },
});

export const setAppMigrationProcessedItemsToImport = (importKey, count) => ({
  type: SET_APP_MIGRATION_PROCESSED_ITEMS_TO_IMPORT,
  payload: { importKey, count },
});

export const setAppMigrationIsNewImport = (flag) => ({
  type: SET_APP_MIGRATION_IS_NEW_IMPORT,
  payload: { flag },
});

export const setAppMigrationCurrentImportKey = (importKey) => ({
  type: SET_APP_MIGRATION_CURRENT_IMPORT_KEY,
  payload: { importKey },
});

export const setAppMigrationFilePackage = ({ filePackage, importKey }) => ({
  type: SET_APP_MIGRATION_FILE_PACKAGE,
  payload: { filePackage, importKey },
});

export const initiateImportProcess = (importKey) => ({
  type: INITIATE_APP_MIGRATION_IMPORT_PROCESS,
  payload: { importKey },
});

export const setImportStep = (step, importKey) => ({
  type: SET_APP_MIGRATION_IMPORT_STEP,
  payload: { step, importKey },
});

export const setAppMigrationZipFileId = (id, importKey) => ({
  type: SET_APP_MIGRATION_ZIP_FIELD_ID,
  payload: { id, importKey },
});

export const clearAppMigrationImportItem = (importKey) => ({
  type: CLEAR_APP_MIGRATION_IMPORT_ITEM,
  payload: { importKey },
});

const setImportLoading = (flag, importKey) => ({
  type: SET_APP_MIGRATION_IMPORT_LOADING,
  payload: { flag, importKey },
});

const setAppMigrationImportData = (importKey, importStatuses) => ({
  type: SET_APP_MIGRATION_IMPORT_DATA,
  payload: { importKey, importStatuses },
});

export const setUploadItems = (importKey, items) => ({
  type: SET_APP_MIGRATION_UPLOAD_ITEMS,
  payload: { importKey, items },
});

export const changeImportStatus = (importKey, id, key, value) => ({
  type: CHANGE_APP_MIGRATION_IMPORT_STATUS,
  payload: { importKey, id, key, value },
});

export const bulkChangeImportStatuses = ({ importKey, key, ids, value }) => ({
  type: BULK_CHANGE_APP_MIGRATION_IMPORT_STATUSES,
  payload: { importKey, key, ids, value },
});

const setImportError = (flag, message, importKey) => ({
  type: SET_APP_MIGRATION_IMPORT_ERROR,
  payload: { flag, message, importKey },
});

const handleImportApiCallFail = ({ errors, dispatch, importKey }) => {
  batch(() => {
    dispatch(handleErrors(errors));
    dispatch(setImportLoading(false, importKey));
  });
};

export const insertActivityLog = async ({ importKey, dispatch, getState, isImportCanceled }) => {
  const { importItems } = getState().appMigration.import;
  const appMigrationZipFileId = importItems[importKey].zipFieldId;

  const insertActivityLogApi = getImportApiCall(APP_MIGRATION_IMPORT_APIS.INSERT_ACTIVITY_LOG);
  const response = await insertActivityLogApi({
    importKey,
    appMigrationZipFileId,
    isImportCanceled,
  });

  if (response.hasError) {
    handleImportApiCallFail({ errors: response.errors, dispatch, importKey });
  }
};

export const checkStatus = ({ importKey, status, processFinished, dispatch, getState }) => {
  switch (status) {
    case STATUSES.IN_PROGRESS: {
      setTimeout(async () => {
        getImportStatus({ importKey, dispatch, getState });
      }, IMPORT_POLLING_TIME);
      return;
    }

    case STATUSES.COMPLETED: {
      if (processFinished) {
        batch(() => {
          dispatch(setImportStep(IMPORT_STEPS.UPLOAD_COMPLETED, importKey));
          dispatch(setImportLoading(false, importKey));
        });
      } else {
        setTimeout(async () => {
          getImportStatus({ importKey, dispatch, getState });
        }, IMPORT_POLLING_TIME);
      }
      return;
    }

    case STATUSES.CANCEL: {
      batch(() => {
        dispatch(setImportStep(IMPORT_STEPS.UPLOAD_COMPLETED, importKey));
        dispatch(setImportLoading(false, importKey));
      });
      return;
    }

    default: {
      batch(() => {
        dispatch(setImportError(true, 'Unexpected Status', importKey));
        dispatch(setImportStep(IMPORT_STEPS.UPLOAD_COMPLETED, importKey));
        dispatch(setImportLoading(false, importKey));
      });
      return;
    }
  }
};

export const getImportStatus = async ({ importKey, dispatch, getState }) => {
  const { importItems } = getState().appMigration.import;
  const { importStatuses: currentImportStatuses, zipFieldId } = importItems[importKey];

  const checkStatusApi = getImportApiCall(APP_MIGRATION_IMPORT_APIS.CHECK_STATUS);
  const response = await checkStatusApi({ importKey, appMigrationZipFileId: zipFieldId });

  if (!response.hasError) {
    const { workItemStatuses: importStatuses, processFinished } = response;

    const preprocessedImportStatuses = updateImportStatusesWithResponse(
      importStatuses,
      currentImportStatuses
    );

    dispatch(setAppMigrationImportData(importKey, preprocessedImportStatuses));
    dispatch(setAppMigrationProcessedItemsToImport(importKey, importStatuses.length));

    const status = getOverallImportStatus(preprocessedImportStatuses);
    checkStatus({ importKey, status, processFinished, dispatch, getState });
  } else {
    handleImportApiCallFail({ errors: response.errors, dispatch, importKey });
  }
};

export const appMigrationImport = ({ importKey }) => async (dispatch, getState) => {
  batch(() => {
    dispatch(setImportLoading(true, importKey));
    dispatch(setImportStep(IMPORT_STEPS.FILE_PROCESSING, importKey));
  });

  const { importItems } = getState().appMigration.import;
  const { importStatuses, filePackage } = importItems[importKey];

  const checkedImportStatuses = importStatuses.filter((s) => s.Checked);
  const importStatusesForRequest = preprocessImportStatusesRequest(checkedImportStatuses);

  dispatch(setAppMigrationImportData(importKey, checkedImportStatuses));
  dispatch(setAppMigrationTotalItemsToImport(importKey, checkedImportStatuses.length));

  const importPayload = {
    file: filePackage,
    request: {
      importKey,
      workItems: importStatusesForRequest,
    },
  };

  const importApi = getImportApiCall(APP_MIGRATION_IMPORT_APIS.IMPORT);
  const response = await importApi(importPayload);

  if (!response.hasError) {
    const { appMigrationZipFileId } = response;

    batch(() => {
      dispatch(setAppMigrationZipFileId(appMigrationZipFileId, importKey));

      setTimeout(async () => {
        getImportStatus({ importKey, dispatch, getState });
      }, IMPORT_POLLING_TIME);

      setTimeout(() => {
        dispatch(getNewNotifications());
      }, REQUEST_NOTIFICATION_DELAY);
    });
  } else {
    handleImportApiCallFail({ errors: response.errors, dispatch, importKey });
  }
};

export const uploadAppMigrationFile = (file, { importKey }) => async (dispatch, getState) => {
  dispatch(setImportLoading(true, importKey));

  const initiateImportApi = getImportApiCall(APP_MIGRATION_IMPORT_APIS.UPLOAD);

  const { currentImportKey } = getState().appMigration.import;

  const response = await initiateImportApi(file);

  if (!response.hasError) {
    const { importKey, configitems: importStatuses } = response;

    batch(() => {
      dispatch(clearAppMigrationImportItem(currentImportKey));
      dispatch(setAppMigrationIsNewImport(false));
      dispatch(setAppMigrationCurrentImportKey(importKey));
    });

    dispatch(initiateImportProcess(importKey));
    dispatch(setImportStep(IMPORT_STEPS.UPLOAD_FILE, importKey));

    dispatch(setAppMigrationFilePackage({ filePackage: file, importKey }));

    const preprocessedImportStatuses = preprocessImportStatusesResponse(importStatuses);

    dispatch(setAppMigrationImportData(importKey, preprocessedImportStatuses));

    dispatch(setImportLoading(false, importKey));
  } else {
    handleImportApiCallFail({ errors: response.errors, dispatch, importKey });
  }
};

export const downloadLogFile = ({ importKey, fileName }) => async (dispatch) => {
  const downloadFileApi = getImportApiCall(APP_MIGRATION_IMPORT_APIS.DOWNLOAD_RESULT);

  const { data, headers } = await downloadFileApi({ importKey, fileName });

  if (data.hasError) {
    dispatch(handleErrors(data.errors));
    return;
  }

  const contentType = headers['content-type'];

  if (contentType.includes('application/json')) {
    try {
      const reader = new FileReader();

      reader.addEventListener('loadend', () => {
        const { errors = [] } = JSON.parse(reader.result);
        dispatch(handleErrors(errors));
      });

      reader.readAsText(data);

      return;
    } catch (e) {
      dispatch(handleErrors([{ errorMessage: e, errorNumber: '' }]));
      return;
    }
  }

  downloadFile(fileName, data);
};

export const cancelAppMigrationFile = ({
  importKey,
  appMigrationZipFileId,
  isCancelExportProcess,
}) => async (dispatch, getState) => {
  dispatch(setImportLoading(true, importKey));

  const cancelImportApi = getImportApiCall(APP_MIGRATION_IMPORT_APIS.CANCEL);
  const response = await cancelImportApi({
    importKey,
    appMigrationZipFileId,
    isCancelExportProcess,
  });

  if (!response.hasError) {
    const { workItemStatuses: importStatuses } = response;

    const { importItems } = getState().appMigration.import;
    const { importStatuses: currentImportStatuses } = importItems[importKey];

    const preprocessedImportStatuses = updateImportStatusesWithCancelResponse(
      importStatuses,
      currentImportStatuses
    );

    dispatch(setAppMigrationImportData(importKey, preprocessedImportStatuses));

    checkStatus({ importKey, status: STATUSES.CANCEL, dispatch, getState });
    insertActivityLog({ importKey, dispatch, getState, isImportCanceled: true });
  } else {
    handleImportApiCallFail({ errors: response.errors, dispatch, importKey });
  }
};
